/**
 * The call controls, drawn once.
 *
 * The dock exists in two places - inside the window and in its own always-on-top
 * window - and they have to be the same object, not two things that resemble
 * each other. They used to draw from different icon sets, so the microphone,
 * the screen and the hang-up glyph were all subtly different shapes depending
 * on which one you were looking at.
 *
 * Only the button and the glyphs live here. What the buttons do is the caller's
 * business: in the app they drive the voice store directly, in the detached
 * window they send a message back to the app that owns the call.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

const S = {
  width: 17,
  height: 17,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round'
} as const;

export const Mic = () => <svg {...S}><rect x="9" y="2.6" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>;
export const MicOff = () => <svg {...S}><path d="M9 5.6a3 3 0 0 1 6 0v5M5 11a7 7 0 0 0 10.5 6M12 18v3M4 4l16 16" /></svg>;
export const Headset = () => <svg {...S}><path d="M4 12v-2a8 8 0 0 1 16 0v2M4 14a2 2 0 0 1 2-2h1v6H6a2 2 0 0 1-2-2v-2ZM20 14a2 2 0 0 0-2-2h-1v6h1a2 2 0 0 0 2-2v-2Z" /></svg>;
export const HeadsetOff = () => <svg {...S}><path d="M4 12v-2a8 8 0 0 1 16 0v2M4 14a2 2 0 0 1 2-2h1v6H6a2 2 0 0 1-2-2v-2ZM20 14a2 2 0 0 0-2-2h-1v6h1a2 2 0 0 0 2-2v-2ZM4 4l16 16" /></svg>;
/* A monitor with the arrow that says this is going out, not coming in. */
export const ScreenIcon = () => <svg {...S}><rect x="2.6" y="4.4" width="18.8" height="12.6" rx="2" /><path d="M8 20.4h8" /><path d="M12 13.8V7.6M9.4 10.2 12 7.4l2.6 2.8" /></svg>;
export const Hangup = () => <svg {...S}><path d="M3 9.5c5-3 13-3 18 0v3.2l-4-.6-.7-2.6a14 14 0 0 0-8.6 0l-.7 2.6-4 .6Z" /></svg>;
/* A gear, because this opens all of settings rather than a mixer. */
export const Sliders = () => <svg {...S}><circle cx="12" cy="12" r="3.1" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1 1.56V21a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 8.9 19.3a1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.7 15a1.7 1.7 0 0 0-1.56-1H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.7 8.9a1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.7a1.7 1.7 0 0 0 1-1.56V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1 1.56 1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.3 9v0a1.7 1.7 0 0 0 1.56 1H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.56 1Z" /></svg>;

/** One control in the dock. The same size, radius and states in both windows. */
export function DockButton({ children, label, on, danger, onClick }: {
  children: ReactNode;
  label: string;
  on?: boolean;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'no-drag grid h-[34px] w-[34px] flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] border-0 bg-transparent',
        'transition-[background,color,transform] duration-150 active:scale-90',
        // Muted and deafened are off states, so they read red - a filled
        // teal box was the loudest thing in the dock and meant the opposite
        // of what it looked like.
        on && !danger ? 'text-bad hover:bg-[color-mix(in_srgb,var(--bad)_14%,transparent)]'
          : danger ? 'text-dim hover:bg-[color-mix(in_srgb,var(--bad)_14%,transparent)] hover:text-bad'
            : 'text-dim hover:bg-surface-3 hover:text-ink'
      )}
    >
      {children}
    </button>
  );
}

/** Three bars reading the round trip. Same meter in both windows. */
export function PingBars({ ping }: { ping: number | null }) {
  const lit = ping === null ? 0 : ping < 80 ? 3 : ping < 180 ? 2 : 1;
  const tone = ping === null ? 'bg-ghost' : ping < 80 ? 'bg-good' : ping < 180 ? 'bg-warn' : 'bg-bad';
  return (
    <span className="flex items-end gap-[2px]">
      {[4, 6, 8].map((h, i) => (
        <i
          key={h}
          className={cn('w-[2px] rounded-full transition-colors', i < lit ? tone : 'bg-surface-4')}
          style={{ height: h }}
        />
      ))}
    </span>
  );
}

/** Move the dock into its own window, and bring it back. */
export const PopOut = () => <svg {...S}><path d="M14 4h6v6M20 4l-8.5 8.5" /><path d="M18 14v5a1.8 1.8 0 0 1-1.8 1.8H5A1.8 1.8 0 0 1 3.2 19V7.8A1.8 1.8 0 0 1 5 6h5" /></svg>;
export const DockIn = () => <svg {...S}><path d="M20.4 3.6 12 12M12 12h5.5M12 12V6.5" /><path d="M18 14v5a1.8 1.8 0 0 1-1.8 1.8H5A1.8 1.8 0 0 1 3.2 19V7.8A1.8 1.8 0 0 1 5 6h5" /></svg>;
